import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App';

// Build stamp — set by scripts/write-version.js (prebuild) so a deployed page
// can be matched to a commit from the console (and to <meta name="version">).
if (process.env.REACT_APP_GIT_SHA) {
  console.info('[nycroyalcarriage] build', process.env.REACT_APP_GIT_SHA, process.env.REACT_APP_BUILD_DATE || '');
}

const rootElement = document.getElementById('root');
const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// react-snap's client DOM has no React 18 Suspense markers. Hydrating that
// snapshot replaces the root and repaints the LCP text. Export a server render
// for the two entry pages once the build browser has loaded their lazy modules.
// Visitors never load this build-only renderer.
if (/ReactSnap/i.test(navigator.userAgent) && /^\/(?:book\/?)?$/.test(window.location.pathname)) {
  import(/* webpackChunkName: "build-prerender" */ 'react-dom/server').then(({ renderToString }) => {
    window.snapSaveState = () => {
      if (!/^\/(?:book\/?)?$/.test(window.location.pathname)) return {};
      // This render runs inside a build browser. Treat Helmet as SSR so the
      // temporary component tree cannot remove the live canonical/meta tags.
      const canUseDOM = HelmetProvider.canUseDOM;
      HelmetProvider.canUseDOM = false;
      try { rootElement.innerHTML = renderToString(app); }
      finally { HelmetProvider.canUseDOM = canUseDOM; }
      return {};
    };
  });
}

if (rootElement.hasChildNodes()) {
  // Pre-rendered HTML from react-snap exists — hydrate to preserve it.
  // This prevents the LCP element from being destroyed and re-created,
  // keeping the hero visible while lazy chunks download.
  const hydrate = () => ReactDOM.hydrateRoot(rootElement, app, {
    onRecoverableError: (error, info) => {
      // Editorial post-renderers can require a client fallback. Expose recovery
      // for diagnostics; entry-page QA requires none and keeps the original H1.
      window.dispatchEvent(new CustomEvent('app:hydration-recovery', {
        detail: { message: error.message, componentStack: info.componentStack },
      }));
    }
  });
  // Let the already styled HTML paint before attaching the component tree.
  // Start immediately on an early interaction; no fixed idle delay or UA test.
  let started = false;
  let frame;
  const events = ['pointerdown', 'keydown', 'focusin'];
  const start = () => {
    if (started) return;
    started = true;
    cancelAnimationFrame(frame);
    events.forEach(event => rootElement.removeEventListener(event, start, true));
    hydrate();
  };
  if (document.visibilityState === 'hidden') start();
  else {
    events.forEach(event => rootElement.addEventListener(event, start, { capture: true, once: true }));
    frame = requestAnimationFrame(() => { frame = requestAnimationFrame(start); });
  }
} else {
  // Dev mode or no pre-rendered content — create fresh root
  ReactDOM.createRoot(rootElement).render(app);
}
